import { Injectable } from '@angular/core';
import { Events } from '../utils/events';
import { AdherenceToNormal } from '../providers/evaluate_brain/anomaly-stats.service';
import { API } from './api';
import { RecognitionFrame } from './frame';
import { BrainVersionType } from '../providers/brain';

import * as MathUtils from '../utils/math';

export interface Bounds {
    min: number | null;
    max: number | null;
}

export interface ClassBoundsMap {
    Normal: Bounds;
    Anomaly: Bounds;
    All: Bounds;
}

export interface AnomalyValidationSliderAsset {
    actualClass: string;
    normalScore: number;
}

@Injectable()
export class AnomalySliderProvider {
    constructor(private api: API, public events: Events) {}

    public async kickoffSaveNormalThresholdJob(
        datasetIdentifier: string,
        brainVersionType: BrainVersionType,
        normalThreshold: number
    ): Promise<string> {
        const result = await this.api.post(`datasets/${datasetIdentifier}/normal-threshold`, {
            normalThreshold,
            brainVersionType,
            runAsJob: true
        });

        return result.jobIdentifier;
    }

    public async kickoffUpdateAdherenceToNormalJob(
        datasetIdentifier: string,
        adherenceToNormal: AdherenceToNormal,
        brainVersionType: BrainVersionType
    ): Promise<string> {
        const result = await this.api.post(`datasets/${datasetIdentifier}/adherence-to-normal?runAsJob=1`, {
            adherenceToNormal,
            createNewBrainVersion: true,
            brainVersionType
        });

        return result.jobIdentifier;
    }

    /**
     * Generate a simple set of slider bounds that encompasses all frames
     */
    public generateSimpleSliderBounds(
        brainVersionType: BrainVersionType,
        frameGTScores: RecognitionFrame[] | AnomalyValidationSliderAsset[],
        paddingPercentage: number = 0
    ): Bounds | null {
        if (!frameGTScores || frameGTScores.length == 0) {
            return null;
        }

        const classBoundsMap = this.generateClassBoundsMap(frameGTScores);

        return this.generateSimpleSliderBoundsFromClassBoundsMap(brainVersionType, classBoundsMap, paddingPercentage);
    }

    private generateSimpleSliderBoundsFromClassBoundsMap(
        brainVersionType: BrainVersionType,
        classBoundsMap: ClassBoundsMap,
        paddingPercentage: number = 0
    ): Bounds | null {
        if (classBoundsMap.All.min === null || classBoundsMap.All.max === null) {
            return null;
        }

        const bounds = this.getBrainVersionTypeBounds(brainVersionType);
        const min = MathUtils.clamp(classBoundsMap.All.min * (1 - paddingPercentage), bounds.min, bounds.max);
        const max = MathUtils.clamp(classBoundsMap.All.max * (1 + paddingPercentage), bounds.min, bounds.max);

        return {
            min: Math.round(min * 1000) / 1000,
            max: Math.round(max * 1000) / 1000
        };
    }

    public generateClassBoundsMap(frameGTScores: RecognitionFrame[] | AnomalyValidationSliderAsset[]): ClassBoundsMap {
        const classBoundsMap: ClassBoundsMap = {
            Normal: {
                min: null,
                max: null
            },
            Anomaly: {
                min: null,
                max: null
            },
            All: {
                min: null,
                max: null
            }
        };

        for (const frameGTScore of frameGTScores) {
            const gt = frameGTScore.actualClass;
            const normalScore = frameGTScore.normalScore;

            if (classBoundsMap[gt] == undefined) {
                continue;
            }

            if (classBoundsMap[gt].min === null || normalScore < classBoundsMap[gt].min) {
                classBoundsMap[gt].min = normalScore;
            }

            if (classBoundsMap[gt].max === null || normalScore > classBoundsMap[gt].max) {
                classBoundsMap[gt].max = normalScore;
            }

            if (classBoundsMap.All.min === null || normalScore < classBoundsMap.All.min) {
                classBoundsMap.All.min = normalScore;
            }

            if (classBoundsMap.All.max === null || normalScore > classBoundsMap.All.max) {
                classBoundsMap.All.max = normalScore;
            }
        }

        return classBoundsMap;
    }

    public toSliderValue(realValue: number): number {
        return Math.round(realValue * 1000);
    }

    // NOTE: The bounds passed in should be rounded using toSliderValue().
    public hasEnoughData(bounds: Bounds): boolean {
        return bounds && bounds.min !== null && bounds.max !== null && bounds.min != bounds.max;
    }

    public getLearningBounds(): Bounds {
        return {
            min: 1,
            max: null
        };
    }

    public getAnomalyHiFiBounds(): Bounds {
        return {
            min: 0,
            max: 1
        };
    }

    public getBrainVersionTypeBounds(brainVersionType: BrainVersionType): Bounds {
        if (brainVersionType == BrainVersionType.AnomalyHiFi) {
            return this.getAnomalyHiFiBounds();
        } else {
            return this.getLearningBounds();
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/anomaly_slider.ts